import { gql } from 'graphql-tag';
import { pick, set } from 'lodash';
import { ActionTree, GetterTree, MutationTree } from 'vuex';
import { RootState } from '~/store';

const initialState = () => ({
  id: null,
  controller: null,
  data: {
    category: {
      count: 0,
      results: []
    },
    merchants: {
      count: 0,
      results: []
    },
    merchant: {}
  },
  table: {
    headers: [
      {
        text: 'Merchant ID',
        align: 'center',
        sortable: false,
        value: 'code'
      },
      {
        text: 'Merchant Logo',
        align: 'center',
        sortable: false,
        value: 'logo'
      },
      {
        text: 'Merchant Name',
        align: 'center',
        sortable: true,
        value: 'name'
      },
      {
        text: 'Code',
        align: 'center',
        sortable: false,
        value: 'label'
      },
      {
        text: 'Category',
        align: 'center',
        sortable: false,
        value: 'categories'
      },
      {
        text: 'Status',
        align: 'center',
        sortable: false,
        value: 'status'
      },
      {
        text: '',
        align: 'center',
        sortable: false,
        value: 'actions'
      }
    ],
    footer: {
      'items-per-page-options': [10, 25, 50, 100],
      'show-current-page': true,
      'show-first-last-page': true
    },
    filter: {
      keyword: '',
      categories: [],
      status: null
    },
    options: {
      page: 1,
      itemsPerPage: 10,
      sortBy: ['name'],
      sortDesc: [false],
      groupBy: [],
      groupDesc: [],
      multiSort: false,
      mustSort: true
    }
  },
  query: {
    where: {},
    sort: {
      name: 'ASC'
    },
    offset: 0,
    limit: 10
  },
  loading: {
    findAll: false,
    findOne: false,
    add: false,
    edit: false,
    update: false,
    category: false
  }
});

export const state = () => initialState();

export type PartnerMerchantState = ReturnType<typeof state>;

export const getters: GetterTree<PartnerMerchantState, RootState> = {
  getId: ({ id }) => id,
  getCategory: ({ data }) => data.category,
  getMerchants: ({ data }) => data.merchants,
  getSelectedMerchant: ({ data }) => data.merchant,
  getQuery: ({ query }) => query,
  getTable: ({ table }) => table,
  getLoading: ({ loading }) => loading
};

export const mutations: MutationTree<PartnerMerchantState> = {
  changeId: (state, id) => (state.id = id),
  changeQuery: (state, query) => Object.assign(state.query, query),
  changeTableFilter: (state, filter) => {
    Object.assign(state.table, { filter });
    Object.assign(state.query, {
      where: {
        name: filter.keyword,
        isActive: filter.status,
        categoryId: filter.categories
      }
    });
  },
  changeTableOptions: (state, options) => {
    Object.assign(state.table, { options });
    Object.assign(state.query, {
      sort: set({}, options.sortBy[0], options.sortDesc[0] ? 'DESC' : 'ASC'),
      limit: options.itemsPerPage,
      offset: (options.page - 1) * options.itemsPerPage
    });
  },
  setCategory: (state, data: PartnerMerchantState['data']['category']) =>
    (state.data.category = data),
  setMerchants: (state, data: PartnerMerchantState['data']['merchants']) =>
    (state.data.merchants = data),
  setSelectedMerchant: (
    state,
    data: PartnerMerchantState['data']['merchant']
  ) => (state.data.merchant = data),
  setLoading: (state, loading: PartnerMerchantState['loading']) =>
    (state.loading = Object.assign({}, state.loading, loading)),
  setController: (state, controller) => (state.controller = controller),
  resetState: (state) => Object.assign(state, initialState())
};

export const actions: ActionTree<PartnerMerchantState, RootState> = {
  /**
   * Fetch merchants.
   *
   * @param {*} { commit, state, dispatch }
   * @param {{ standalone: boolean; query: PartnerMerchantState['query'] }} [props] Prop options;
   *
   * @description `
   *  props to be used on other components without disturbing the state
   * `
   * @return {*}
   */
  async findAll(
    { commit, state, dispatch },
    props?: { standalone: boolean; query: PartnerMerchantState['query'] }
  ) {
    if (process.server) return;

    dispatch('controllerInit');

    const api = window.$nuxt.$options.$api;
    const emitter = window.$nuxt.$options.$emitter;

    try {
      commit('setLoading', { findAll: true });

      let variables = state.query;
      if (props?.standalone) variables = props.query;
      const { data } = await api.query({
        query: gql`
          query MerchantFindAll(
            $where: MerchantQueryFilter
            $sort: MerchantQuerySortBy
            $offset: Int
            $limit: Int
          ) {
            Merchant {
              findAllV2(
                where: $where
                sort: $sort
                offset: $offset
                limit: $limit
              ) {
                count
                results {
                  id
                  code
                  label
                  logo: logoUrl
                  name
                  isActive
                  categories {
                    id
                    name
                  }
                }
              }
            }
          }
        `,
        variables,
        context: {
          fetchOptions: {
            signal: state.controller.signal
          }
        }
      });
      const merchant = data?.Merchant?.findAllV2;
      commit('setLoading', { findAll: false });
      if (props?.standalone) {
        return { status: true, message: null, data: merchant };
      } else {
        commit('setMerchants', pick(merchant, ['count', 'results']));
      }
    } catch (error) {
      commit('setLoading', { findAll: false });
      if (!/signal is aborted without reason/i.test(error.toString()))
        emitter.emit('TOAST', error);
      if (props?.standalone) {
        return { status: false, message: error, data: null };
      } else {
        commit('setMerchants', { count: 0, results: [] });
      }
    }
  },

  /**
   * Find one merchant.
   */
  async findOne(
    { commit, state, dispatch },
    props?: { id: string; standalone?: boolean }
  ) {
    if (process.server) return;

    dispatch('controllerInit');

    const api = window.$nuxt.$options.$api;
    const emitter = window.$nuxt.$options.$emitter;

    try {
      commit('setLoading', { findOne: true });
      commit('setSelectedMerchant', {});

      const variables = { id: props.id };
      const { data } = await api.query({
        query: gql`
          query MerchantFindOne($id: String!) {
            Merchant {
              findOneV2(id: $id) {
                id
                name
                logoUrl
                label
                address
                province
                city
                district
                postalCode
                website
                picName
                picPhone
                picEmail
                internalRepresentative
                keyword
                relationshipSince
                partnershipType
                partnershipDurationStartAt
                partnershipDurationEndAt
                description
                isActive
                categories {
                  id
                  name
                }
              }
            }
          }
        `,
        variables,
        context: {
          fetchOptions: {
            signal: state.controller.signal
          }
        }
      });
      const merchant = data?.Merchant?.findOneV2;
      commit('setLoading', { findOne: false });
      if (props?.standalone) {
        return { status: true, message: null, data: merchant };
      } else {
        commit('setSelectedMerchant', merchant);
      }
    } catch (error) {
      commit('setLoading', { findOne: false });
      if (!/signal is aborted without reason/i.test(error.toString()))
        emitter.emit('TOAST', error);
      if (props?.standalone) {
        return { status: false, message: error, data: null };
      }
    }
  },

  /**
   * Fetch categories.
   *
   * Purpose:
   *  - filter category list
   */
  async fetchCategory({ commit, state, dispatch }) {
    if (process.server) return;

    dispatch('controllerInit');

    const api = window.$nuxt.$options.$api;
    const emitter = window.$nuxt.$options.$emitter;

    try {
      commit('setLoading', { category: true });

      const { data } = await api.query({
        query: gql`
          query MerchantCategories(
            $sort: MerchantCategoryQuerySortBy
            $where: MerchantCategoryQueryFilter
            $limit: Int
            $offset: Int
          ) {
            Merchant {
              MerchantCategory {
                id
                findAll(
                  sort: $sort
                  where: $where
                  limit: $limit
                  offset: $offset
                ) {
                  count
                  results {
                    id
                    name
                  }
                }
              }
            }
          }
        `,
        variables: { sort: { name: 'ASC' } },
        context: {
          fetchOptions: {
            signal: state.controller.signal
          }
        }
      });
      const category = data?.Merchant?.MerchantCategory?.findAll;
      commit('setCategory', pick(category, ['count', 'results']));
    } catch (error) {
      if (!/signal is aborted without reason/i.test(error.toString()))
        emitter.emit('TOAST', error);
    } finally {
      commit('setLoading', { category: false });
    }
  },

  /**
   * Add merchant mutation.
   */
  async addMerchant({ commit }, { form }: { form: Record<string, any> }) {
    if (process.server) return;

    const api = window.$nuxt.$options.$api;

    try {
      commit('setLoading', { add: true });

      await api.mutate({
        mutation: gql`
          mutation AddMerchant($req: MerchantFormV2!) {
            Merchant {
              addV2(req: $req) {
                id
              }
            }
          }
        `,
        variables: { req: form }
      });

      commit('setLoading', { add: false });
      return { status: true, message: '' };
    } catch (error) {
      commit('setLoading', { add: false });
      return { status: false, message: error };
    }
  },

  /**
   * Edit merchant mutation.
   */
  async editMerchant(
    { commit },
    { id, form }: { id: string; form: Record<string, any> }
  ) {
    if (process.server) return;

    const api = window.$nuxt.$options.$api;

    try {
      commit('setLoading', { edit: true });

      await api.mutate({
        mutation: gql`
          mutation EditMerchant($id: String!, $req: MerchantFormV2!) {
            Merchant {
              editV2(id: $id, req: $req) {
                id
              }
            }
          }
        `,
        variables: { id, req: form }
      });

      commit('setLoading', { edit: false });
      return { status: true, message: '' };
    } catch (error) {
      commit('setLoading', { edit: false });
      return { status: false, message: error };
    }
  },

  /**
   * Creates a new AbortController object instance.
   */
  controllerInit({ commit, state }) {
    if (!state.controller) commit('setController', new AbortController());
  },

  /**
   * Controller abort for canceled request.
   */
  controllerAbort({ commit, state }) {
    if (state.controller) {
      state.controller.abort();
      commit('setController', null);
    }
  }
};
